import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { compactZonedDate, zonedDate } from 'global/helpers/dates'
import i18n from 'i18n'
import { processAttachments } from 'screens/StatusManager/StatusManager.service'
import type {
  BulkDocumentsUploadFormValues,
  BulkStatusUpdateFormValues,
  DocumentsUploadFormValues,
  StatusUpdateFormValues,
  StatusUpdateWithAttachmentsFormValues,
} from 'screens/StatusManager/StatusManager.types'
import {
  type AvailableStopStatus,
  type LegData,
  type StopData,
  type StopLegPair,
  type TripLogData,
  WorkflowType,
} from 'screens/TripDetails/TripDetails.types'

export const getUpdatedStopForUpdateStopStatus = (
  stop: StopData,
  data: StatusUpdateFormValues,
): StopData => ({
  ...stop,
  ...generateStatusData(stop, data),
})

export const getUpdatedStopForUpdateStopStatusWithAttachments = (
  stop: StopData,
  data: StatusUpdateWithAttachmentsFormValues,
): StopData => ({
  ...stop,
  ...generateStatusData(stop, data),
  ...generateAttachmentsData(stop, data),
})

export const getUpdatedStopsForBulkUpdateStopStatus = (
  stopLegPairs: StopLegPair[],
  data: BulkStatusUpdateFormValues,
): StopData[] =>
  stopLegPairs
    .filter((stopLegPair) => data.shipmentNumbers.includes(stopLegPair.leg.shipmentNumber))
    .map((stopLegPair) => ({
      ...stopLegPair.stop,
      ...generateStatusData(stopLegPair.stop, data),
    }))

export const getUpdatedStopForAttachFiles = (
  stop: StopData,
  data: DocumentsUploadFormValues,
): StopData => ({
  ...stop,
  ...generateAttachmentsData(stop, data),
})

export const getUpdatedStopsForAttachFiles = (
  stopLegPairs: StopLegPair[],
  data: BulkDocumentsUploadFormValues,
): StopData[] =>
  stopLegPairs
    .filter((stopLegPair) => data.shipmentNumbers.includes(stopLegPair.leg.shipmentNumber))
    .map((stopLegPair) => ({
      ...stopLegPair.stop,
      ...generateAttachmentsData(stopLegPair.stop, data),
    }))

export const updateTripLegsCacheWithUpdatedStops = async (
  updatedStops: StopData[],
  queryKey: QueryKey,
  queryClient: QueryClient,
): Promise<void> => {
  await queryClient.cancelQueries({ queryKey })
  const updatedStopsByCid = new Map(
    updatedStops.map((stop) => [stop.wayPointCid, stop]),
  )
  queryClient.setQueryData<LegData[]>(queryKey, (legs) => legs?.map((leg) => ({
    ...leg,
    wayPoints: leg.wayPoints.map((stop) => updatedStopsByCid.get(stop.wayPointCid) ?? stop),
  })))
}

export const updateTripLegsCacheWithUpdatedLegs = async (
  updatedLegs: LegData[],
  queryKey: QueryKey,
  queryClient: QueryClient,
): Promise<void> => {
  await queryClient.cancelQueries({ queryKey })
  const updatedLegsByShipmentNumber = new Map(
    updatedLegs.map((leg) => [leg.shipmentNumber, leg]),
  )
  queryClient.setQueryData<LegData[]>(queryKey, (legs) =>
    legs?.map((leg) => updatedLegsByShipmentNumber.get(leg.shipmentNumber) ?? leg),
  )
}

const generateStatusData = (
  stop: StopData,
  data: StatusUpdateFormValues | StatusUpdateWithAttachmentsFormValues | BulkStatusUpdateFormValues,
): Partial<StopData> => ({
  statuses: [
    ...stop.statuses,
    {
      id: data.reasonCode ? `${data.statusCode}_${data.reasonCode}` : data.statusCode,
      statusCode: data.statusCode,
      reasonCode: data.reasonCode,
      createdAt: data.createdAt!, // NOTE: it's no longer undefined at this point
      comment: data.comment,
    },
  ],
})

const generateAttachmentsData = (
  stop: StopData,
  data: DocumentsUploadFormValues | StatusUpdateWithAttachmentsFormValues,
): Partial<StopData> => {
  const processedAttachments = processAttachments(data).map((attachment) => ({
    id: attachment.fileId,
    name: '...',
    createdAt: 'pending',
    checksum: 'pending',
    fileType: attachment.fileType,
  }))
  return {
    documents: [...(stop.documents ?? []), ...processedAttachments],
  }
}

export const getTimezoneReference = (stop: StopData, leg: LegData) => {
  // customs stops don't have date windows, so grab the date from the related pickup
  if (stop.type === 'CUS') {
    return leg.wayPoints.find((stop) => stop.type === 'PUP')?.earlyDateTime ?? ''
  }
  return stop.earlyDateTime
}

export const getTruckZoneMessage = (
  tripLogs: TripLogData[],
  stopType: 'PUP' | 'DEL' | 'CUS',
  wayPointCid: string,
): string | null => {
  const matchingTripLogs = tripLogs
    .filter((tripLog) => tripLog.data?.wayPointCid === wayPointCid)

  let firstArrived: TripLogData | undefined
  let lastDeparted: TripLogData | undefined
  for (const tripLog of matchingTripLogs) {
    if (tripLog.type === 'ARRIVED_AT_LOCATION' && (!firstArrived || firstArrived.createdAt > tripLog.createdAt)) {
      firstArrived = tripLog
    }
    if (tripLog.type === 'DEPARTED_FROM_LOCATION' && (!lastDeparted || lastDeparted.createdAt < tripLog.createdAt)) {
      lastDeparted = tripLog
    }
  }

  if (firstArrived && lastDeparted) {
    switch (stopType) {
      case 'PUP':
        return i18n.t('screens.cs.trip_details.update_status.truck_location_pickup_zone_message', {
          radius: '500m',
          fromDate: compactZonedDate(firstArrived.createdAt, firstArrived.data?.offset),
          toDate: compactZonedDate(lastDeparted.createdAt, lastDeparted.data?.offset),
        })
      case 'DEL':
        return i18n.t('screens.cs.trip_details.update_status.truck_location_delivery_zone_message', {
          radius: '500m',
          fromDate: compactZonedDate(firstArrived.createdAt, firstArrived.data?.offset),
          toDate: compactZonedDate(lastDeparted.createdAt, lastDeparted.data?.offset),
        })
    }
  } else if (firstArrived && !lastDeparted) {
    return i18n.t('screens.cs.trip_details.update_status.truck_location_arrived_zone_message', {
      radius: '500m',
      date: compactZonedDate(firstArrived.createdAt, firstArrived.data?.offset),
    })
  } else if (!firstArrived && lastDeparted) {
    return i18n.t('screens.cs.trip_details.update_status.truck_location_departed_zone_message', {
      radius: '500m',
      date: compactZonedDate(lastDeparted.createdAt, lastDeparted.data?.offset),
    })
  }
  return null
}

export const getUniqueAvailableStatuses = (availableStatuses: AvailableStopStatus[]) => {
  const uniqueStatuses = new Map<string, AvailableStopStatus>()
  availableStatuses.forEach((status) => uniqueStatuses.set(status.id, status))
  return Array.from(uniqueStatuses.values())
}

export const getDefaultStatus = (availableStatuses: AvailableStopStatus[]): string => {
  if (availableStatuses.length === 1) {
    return availableStatuses[0]?.id ?? ''
  }
  return ''
}

export const getDefaultStatusDate = (stop: StopData, leg: LegData) => {
  const stopTimezoneReference = getTimezoneReference(stop, leg)
  if (leg.workflowType === WorkflowType.SECURE_CLOUD_DEPLOYMENT) {
    const stopTimezone = zonedDate(stopTimezoneReference, 'offset') ?? 'Z'
    const currentDateInStopTimezone = zonedDate(new Date(), 'iso_no_tz', stopTimezone)
    return new Date(currentDateInStopTimezone!)
  }
  return new Date(stopTimezoneReference)
}

export const getDefaultStatusTime = (stop: StopData, leg: LegData) => {
  if (leg.workflowType === WorkflowType.SECURE_CLOUD_DEPLOYMENT) {
    return getDefaultStatusDate(stop, leg)
  }
  return null
}
